import { defineStore } from 'pinia'
import { computed, ref } from 'vue'

const THEME_STORAGE_KEY = 'psnap:theme'
const DEFAULT_THEME = 'snap-teal'

const THEME_DEFINITIONS = Object.freeze([
  {
    id: 'snap-teal',
    label: 'Snap Teal',
    primary: '13 148 136',
    secondary: '14 165 233',
    accent: '245 158 11',
    preview: { primary: '#0d9488', secondary: '#0ea5e9', accent: '#f59e0b' },
  },
  {
    id: 'midnight-indigo',
    label: 'Midnight Indigo',
    primary: '99 102 241',
    secondary: '129 140 248',
    accent: '196 181 253',
    preview: { primary: '#6366f1', secondary: '#818cf8', accent: '#c4b5fd' },
  },
  {
    id: 'graphite-slate',
    label: 'Graphite Slate',
    primary: '71 85 105',
    secondary: '100 116 139',
    accent: '14 165 233',
    preview: { primary: '#475569', secondary: '#64748b', accent: '#0ea5e9' },
  },
  {
    id: 'forest-emerald',
    label: 'Forest Emerald',
    primary: '5 150 105',
    secondary: '16 185 129',
    accent: '234 179 8',
    preview: { primary: '#059669', secondary: '#10b981', accent: '#eab308' },
  },
  {
    id: 'sunset-coral',
    label: 'Sunset Coral',
    primary: '234 88 12',
    secondary: '249 115 22',
    accent: '14 165 233',
    preview: { primary: '#ea580c', secondary: '#f97316', accent: '#0ea5e9' },
  },
])

function getStoredTheme() {
  try {
    if (typeof window === 'undefined') return DEFAULT_THEME
    const stored = window.localStorage.getItem(THEME_STORAGE_KEY)
    if (stored && THEME_DEFINITIONS.some(t => t.id === stored)) return stored
  } catch { /* ignore */ }
  return DEFAULT_THEME
}

function persistTheme(themeId) {
  try {
    if (typeof window === 'undefined') return
    window.localStorage.setItem(THEME_STORAGE_KEY, themeId)
  } catch { /* ignore */ }
}

function applyThemeToDOM(themeId) {
  if (typeof document === 'undefined') return
  const def = THEME_DEFINITIONS.find(t => t.id === themeId)
  if (!def) return
  const root = document.documentElement
  root.setAttribute('data-theme', themeId)
  root.style.setProperty('--color-primary', def.primary)
  root.style.setProperty('--color-secondary', def.secondary)
  root.style.setProperty('--color-accent', def.accent)
}

export const useThemeStore = defineStore('theme', () => {
  const currentTheme = ref(getStoredTheme())
  const themes = THEME_DEFINITIONS

  const activeTheme = computed(
    () => THEME_DEFINITIONS.find(t => t.id === currentTheme.value) || THEME_DEFINITIONS[0]
  )

  function setTheme(themeId) {
    if (!THEME_DEFINITIONS.some(t => t.id === themeId)) return
    currentTheme.value = themeId
    persistTheme(themeId)
    applyThemeToDOM(themeId)
  }

  function initialize() {
    applyThemeToDOM(currentTheme.value)
  }

  return { currentTheme, themes, activeTheme, setTheme, initialize }
})
